import type { WorkflowSelectOption } from '../../../types/workflow_automation';
import type { WorkflowConditionCatalogEntry } from './conditions';
import type {
  WorkflowConfigurationOptions,
  WorkflowModelOption,
} from './configurationOptions';
import type { WorkflowTaskConfig } from './tasks';

// Raw option shapes as they arrive from the backend before normalization.
// The backend uses `id` for status options and `key` for strategy options.
type RawOptionById = { id: string; label: string };
type RawOptionByKey = { key: string; label: string };
type RawOption = RawOptionById | RawOptionByKey;

/**
 * Replaces every WorkflowSelectOption[] field in T with RawOption[], per union member.
 * The `T extends infer U` wrapper makes this distribute over union types, so each
 * member is mapped independently. New catalog entry types require no changes here.
 */
type WithRawOptions<T> = T extends infer U
  ? { [K in keyof U]: U[K] extends WorkflowSelectOption[] ? RawOption[] : U[K] }
  : never;

export type WorkflowRawConfigurationOptions = {
  model_options: WorkflowModelOption[];
  condition_options: WithRawOptions<WorkflowConditionCatalogEntry>[];
  trigger_options: WithRawOptions<WorkflowConditionCatalogEntry>[];
  task_options: WithRawOptions<WorkflowTaskConfig>[];
};

const normalizeOption = (option: RawOption): WorkflowSelectOption => ({
  value: 'id' in option ? option.id : option.key,
  label: option.label,
});

const normalizeConditionEntry = (
  entry: WithRawOptions<WorkflowConditionCatalogEntry>
): WorkflowConditionCatalogEntry => {
  switch (entry.operator) {
    case 'in':
    case 'is':
      return { ...entry, options: entry.options.map(normalizeOption) };
    case 'does not change':
      return entry;
  }
};

const normalizeTaskEntry = (
  entry: WithRawOptions<WorkflowTaskConfig>
): WorkflowTaskConfig => {
  switch (entry.type) {
    case 'message reminder':
      return { ...entry, assignee_options: entry.assignee_options.map(normalizeOption) };
  }
};

export const normalizeConfigurationOptions = (
  raw: WorkflowRawConfigurationOptions
): WorkflowConfigurationOptions => ({
  model_options: raw.model_options,
  condition_options: raw.condition_options.map(normalizeConditionEntry),
  trigger_options: raw.trigger_options.map(normalizeConditionEntry),
  task_options: raw.task_options.map(normalizeTaskEntry),
});
